<template>
  <main class="transactions-page">
    <header class="transactions-header">
      <div>
        <span class="transactions-eyebrow">Seus lançamentos</span>
        <h1>Gastos e entradas</h1>
        <p>Acompanhe o que entrou e saiu sem perder nenhum detalhe.</p>
      </div>

      <div class="transactions-header__actions">
        <FinancialPrivacyButton />
        <button
          class="refresh-button"
          type="button"
          :disabled="getLoadingFinance"
          aria-label="Atualizar lançamentos"
          title="Atualizar lançamentos"
          @click="loadData"
        >
          <i class="ph ph-arrow-clockwise" :class="{ 'is-spinning': getLoadingFinance }" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <section class="create-actions" aria-label="Cadastrar movimentação">
      <button class="create-action create-action--expense" type="button" @click="openTransactionModal('expense')">
        <span class="create-action__icon"><i class="ph ph-arrow-up-right" aria-hidden="true"></i></span>
        <span>
          <small>Registrar</small>
          <strong>Novo gasto</strong>
        </span>
      </button>

      <button class="create-action create-action--income" type="button" @click="openTransactionModal('income')">
        <span class="create-action__icon"><i class="ph ph-arrow-down-left" aria-hidden="true"></i></span>
        <span>
          <small>Registrar</small>
          <strong>Nova entrada</strong>
        </span>
      </button>
    </section>

    <section class="transactions-panel" aria-labelledby="transactions-list-title">
      <div class="transactions-toolbar">
        <div>
          <span class="transactions-eyebrow">Movimentações</span>
          <h2 id="transactions-list-title">Histórico</h2>
          <span class="transactions-toolbar__summary">{{ paginationSummary }}</span>
        </div>

        <button
          class="filter-toggle"
          type="button"
          :aria-expanded="showFilters"
          aria-controls="transaction-filters"
          @click="showFilters = !showFilters"
        >
          <i class="ph ph-sliders-horizontal" aria-hidden="true"></i>
          <span>{{ activePeriodLabel }}</span>
          <i class="ph" :class="showFilters ? 'ph-caret-up' : 'ph-caret-down'" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="showFilters" id="transaction-filters" class="filters-panel">
        <label>
          <span>Mês</span>
          <select v-model="filters.month" class="form-select">
            <option :value="null">Todos os meses</option>
            <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
          </select>
        </label>

        <label>
          <span>Ano</span>
          <input v-model="filters.year" type="number" min="2000" max="2100" class="form-control" placeholder="Ano" />
        </label>

        <div class="filters-panel__actions">
          <button class="filter-reset" type="button" @click="resetFilters">Mês atual</button>
          <button class="filter-apply" type="button" @click="applyFilters">
            Aplicar filtro
            <i class="ph ph-arrow-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div v-if="getFinanceError" class="transactions-feedback transactions-feedback--error" role="alert">
        <div>
          <i class="ph ph-warning-circle" aria-hidden="true"></i>
          <span>{{ getFinanceError }}</span>
        </div>
        <button type="button" @click="loadData">Tentar novamente</button>
      </div>

      <div v-if="getLoadingFinance && !getFinanceTransactions.length" class="transaction-skeletons" aria-label="Carregando lançamentos">
        <div v-for="item in 5" :key="item" class="transaction-skeleton">
          <span class="skeleton skeleton--icon"></span>
          <span class="skeleton skeleton--content"></span>
          <span class="skeleton skeleton--amount"></span>
        </div>
      </div>

      <template v-else-if="getFinanceTransactions.length">
        <div class="desktop-transactions-table">
          <table class="finance-data-table">
            <thead>
              <tr>
                <th scope="col">Descrição</th>
                <th scope="col">Categoria</th>
                <th scope="col">Origem</th>
                <th scope="col">Data</th>
                <th scope="col" class="text-end">Valor</th>
                <th scope="col" class="text-end"><span class="visually-hidden">Ações</span></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="transaction in getFinanceTransactions"
                :key="transaction.id"
                class="transaction-table-row"
                tabindex="0"
                :aria-label="`Ver detalhes de ${transactionDescription(transaction)}`"
                @click="openTransactionDetails(transaction)"
                @keydown.enter="openTransactionDetails(transaction)"
              >
                <td>
                  <div class="table-description">
                    <span class="transaction-type-icon" :class="`is-${transaction.type}`" :style="transactionIconStyle(transaction)">
                      <i :class="transaction.type === 'income' ? 'ph ph-arrow-down-left' : 'ph ph-arrow-up-right'" aria-hidden="true"></i>
                    </span>
                    <div>
                      <strong :title="transactionDescription(transaction)">{{ transactionDescription(transaction) }}</strong><small v-if="$store.getters.getCouple">{{ transaction.owner?.name }}</small>
                      <small v-if="transaction.is_recurring">Recorrente</small>
                    </div>
                  </div>
                </td>
                <td>{{ transaction.type === 'income' ? 'Não se aplica' : (transaction.category?.name || 'Sem categoria') }}</td>
                <td><span class="source-badge">{{ sourceLabel(transaction.source) }}</span></td>
                <td>{{ formatDateTime(transaction.occurred_at) }}</td>
                <td class="transaction-amount text-end" :class="`is-${transaction.type}`">
                  {{ privateValue(`${transaction.type === 'income' ? '+' : '-'}${money(transaction.amount)}`) }}
                </td>
                <td class="text-end">
                  <button
                    v-if="transaction.can_edit !== false" class="delete-button"
                    type="button"
                    aria-label="Remover lançamento"
                    title="Remover lançamento"
                    @click.stop="requestDelete(transaction)"
                  >
                    <i class="ph ph-trash" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mobile-transactions-list">
          <section v-for="group in transactionGroups" :key="group.key" class="transaction-group">
            <h3>{{ group.label }}</h3>

            <article
              v-for="transaction in group.items"
              :key="transaction.id"
              class="transaction-item"
              role="button"
              tabindex="0"
              :aria-label="`Ver detalhes de ${transactionDescription(transaction)}`"
              @click="openTransactionDetails(transaction)"
              @keydown.enter="openTransactionDetails(transaction)"
            >
              <span class="transaction-type-icon" :class="`is-${transaction.type}`" :style="transactionIconStyle(transaction)">
                <i :class="transaction.type === 'income' ? 'ph ph-arrow-down-left' : 'ph ph-arrow-up-right'" aria-hidden="true"></i>
              </span>

              <div class="transaction-item__main">
                <strong :title="transactionDescription(transaction)">{{ transactionDescription(transaction) }}</strong><small v-if="$store.getters.getCouple">{{ transaction.owner?.name }}</small>
                <span>
                  {{ transaction.type === 'income' ? 'Não se aplica' : (transaction.category?.name || 'Sem categoria') }}
                  <span aria-hidden="true">•</span>
                  {{ sourceLabel(transaction.source) }}
                  <span aria-hidden="true">•</span>
                  {{ formatTime(transaction.occurred_at) }}
                </span>
                <small v-if="transaction.is_recurring">Recorrente</small>
              </div>

              <div class="transaction-item__side">
                <strong class="transaction-amount" :class="`is-${transaction.type}`">
                  {{ privateValue(`${transaction.type === 'income' ? '+' : '-'}${money(transaction.amount)}`) }}
                </strong>
                <button
                  v-if="transaction.can_edit !== false" class="delete-button"
                  type="button"
                  aria-label="Remover lançamento"
                  title="Remover lançamento"
                  @click.stop="requestDelete(transaction)"
                >
                  <i class="ph ph-trash" aria-hidden="true"></i>
                </button>
              </div>
            </article>
          </section>
        </div>

        <div class="pagination-row">
          <span class="pagination-row__summary">{{ paginationSummary }}</span>

          <div class="pagination-actions">
            <button type="button" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">Anterior</button>
            <button
              v-for="page in paginationPages"
              :key="page"
              type="button"
              :class="{ active: page === currentPage }"
              :aria-current="page === currentPage ? 'page' : undefined"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
            <button type="button" :disabled="currentPage >= lastPage" @click="goToPage(currentPage + 1)">Próxima</button>
          </div>

          <div class="pagination-mobile">
            <button type="button" aria-label="Página anterior" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
              <i class="ph ph-caret-left" aria-hidden="true"></i>
            </button>
            <span>Página {{ currentPage }} de {{ lastPage }}</span>
            <button type="button" aria-label="Próxima página" :disabled="currentPage >= lastPage" @click="goToPage(currentPage + 1)">
              <i class="ph ph-caret-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </template>

      <div v-else class="empty-transactions">
        <span class="empty-transactions__icon"><i class="ph ph-receipt" aria-hidden="true"></i></span>
        <strong>Nenhum lançamento neste período</strong>
        <p>Registre um gasto ou uma entrada para começar seu histórico.</p>
        <button type="button" @click="openTransactionModal('expense')">Registrar primeiro gasto</button>
      </div>
    </section>

    <div v-if="showTransactionModal" class="transaction-modal" role="dialog" aria-modal="true" aria-labelledby="transactionModalTitle">
      <div class="transaction-modal__backdrop" @click="closeTransactionModal"></div>
      <form class="transaction-modal__content" @submit.prevent="submitTransaction">
        <span class="transaction-modal__handle" aria-hidden="true"></span>

        <div class="transaction-modal__header">
          <div>
            <span class="transactions-eyebrow">Novo lançamento</span>
            <h2 id="transactionModalTitle">{{ modalTitle }}</h2>
          </div>
          <button class="transaction-modal__close" type="button" aria-label="Fechar" @click="closeTransactionModal">
            <i class="ph ph-x" aria-hidden="true"></i>
          </button>
        </div>

        <div class="transaction-type-selector" aria-label="Tipo do lançamento">
          <button type="button" :class="{ active: form.type === 'expense' }" @click="form.type = 'expense'">
            <i class="ph ph-arrow-up-right" aria-hidden="true"></i>
            Gasto
          </button>
          <button type="button" :class="{ active: form.type === 'income' }" @click="form.type = 'income'">
            <i class="ph ph-arrow-down-left" aria-hidden="true"></i>
            Entrada
          </button>
        </div>

        <label class="amount-field">
          <span>Valor</span>
          <div>
            <span>R$</span>
            <input
              v-model="amountInput"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              placeholder="0,00"
              aria-label="Valor do lançamento"
              required
              @input="sanitizeAmountInput"
              @paste.prevent="handleAmountPaste"
            />
          </div>
        </label>

        <div class="transaction-form-grid">
          <label v-if="form.type === 'expense'" class="form-field">
            <span>Categoria</span>
            <select v-model="form.category_id" class="form-select">
              <option :value="null">Outros (automático)</option>
              <option v-for="category in getFinanceCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
            </select>
          </label>

          <label class="form-field" :class="{ 'form-field--wide': form.type === 'income' }">
            <span>Data e hora</span>
            <input v-model="form.occurred_at" type="datetime-local" class="form-control" />
          </label>
        </div>

        <label class="form-field">
          <span>Descrição</span>
          <input v-model="form.description" type="text" class="form-control" placeholder="Ex: mercado, aluguel, salário" />
        </label>

        <label class="recurring-check">
          <input v-model="form.is_recurring" class="form-check-input" type="checkbox" />
          <span>Este lançamento é recorrente</span>
        </label>

        <button class="save-transaction-button" type="submit" :disabled="saving">
          <span>{{ saving ? 'Salvando...' : modalSubmitLabel }}</span>
          <i v-if="!saving" class="ph ph-arrow-right" aria-hidden="true"></i>
        </button>

        <div v-if="feedback" class="transactions-feedback mt-15 mb-0" :class="feedbackClass">{{ feedback }}</div>
      </form>
    </div>

    <div
      v-if="selectedTransaction"
      class="transaction-detail"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transactionDetailTitle"
    >
      <div class="transaction-detail__backdrop" @click="closeTransactionDetails"></div>
      <section class="transaction-detail__content">
        <span class="transaction-detail__handle" aria-hidden="true"></span>

        <header class="transaction-detail__header">
          <div>
            <span class="transactions-eyebrow">Detalhes</span>
            <h2 id="transactionDetailTitle">Lançamento</h2><small>{{ selectedTransaction.owner?.name }}</small>
          </div>
          <div class="transaction-detail__header-actions">
            <button
              v-if="!detailEditing && selectedTransaction.can_edit !== false"
              class="transaction-detail__edit-button"
              type="button"
              title="Editar lançamento"
              @click="startTransactionEdit"
            >
              <i class="ph ph-pencil-simple" aria-hidden="true"></i>
              <span>Editar</span>
            </button>
            <button type="button" aria-label="Fechar detalhes" @click="closeTransactionDetails">
              <i class="ph ph-x" aria-hidden="true"></i>
            </button>
          </div>
        </header>

        <form v-if="detailEditing" class="transaction-detail__edit-form" @submit.prevent="saveTransactionEdit">
          <label class="amount-field transaction-detail__amount-field">
            <span>Valor</span>
            <div>
              <span>R$</span>
              <input
                v-model="detailAmountInput"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                placeholder="0,00"
                aria-label="Valor do lançamento"
                required
                @input="sanitizeDetailAmountInput"
                @paste.prevent="handleDetailAmountPaste"
              />
            </div>
          </label>

          <label v-if="selectedTransaction.type === 'expense'" class="form-field">
            <span>Categoria</span>
            <select v-model="detailForm.category_id" class="form-select">
              <option :value="null">Outros (automático)</option>
              <option v-for="category in getFinanceCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
            </select>
          </label>

          <label class="form-field">
            <span>Descrição</span>
            <textarea
              v-model="detailForm.description"
              class="form-control"
              rows="3"
              maxlength="255"
              placeholder="Descreva este lançamento"
            ></textarea>
          </label>

          <dl class="transaction-detail__grid transaction-detail__grid--metadata" aria-label="Informações não editáveis">
            <div aria-disabled="true">
              <dt>Data e hora</dt>
              <dd>{{ formatDateTime(selectedTransaction.occurred_at) }}</dd>
            </div>
            <div aria-disabled="true">
              <dt>Origem</dt>
              <dd>{{ sourceLabel(selectedTransaction.source) }}</dd>
            </div>
          </dl>

          <div class="transaction-detail__edit-actions">
            <button type="button" :disabled="detailSaving" @click="cancelTransactionEdit">Cancelar</button>
            <button class="is-primary" type="submit" :disabled="detailSaving">
              <span>{{ detailSaving ? 'Salvando...' : 'Salvar alterações' }}</span>
              <i v-if="!detailSaving" class="ph ph-check" aria-hidden="true"></i>
            </button>
          </div>

          <div v-if="detailFeedback" class="transactions-feedback transactions-feedback--error mb-0" role="alert">
            {{ detailFeedback }}
          </div>
        </form>

        <template v-else>
          <div class="transaction-detail__amount" :class="`is-${selectedTransaction.type}`">
            <span class="transaction-type-icon" :class="`is-${selectedTransaction.type}`" :style="transactionIconStyle(selectedTransaction)">
              <i :class="selectedTransaction.type === 'income' ? 'ph ph-arrow-down-left' : 'ph ph-arrow-up-right'" aria-hidden="true"></i>
            </span>
            <div>
              <span>{{ selectedTransaction.type === 'income' ? 'Entrada' : 'Gasto' }}</span>
              <strong>
                {{ privateValue(`${selectedTransaction.type === 'income' ? '+' : '-'}${money(selectedTransaction.amount)}`) }}
              </strong>
            </div>
          </div>

          <dl class="transaction-detail__grid">
            <div>
              <dt>Descrição</dt>
              <dd>{{ transactionDescription(selectedTransaction) }}</dd>
            </div>
            <div>
              <dt>Categoria</dt>
              <dd>{{ selectedTransaction.type === 'income' ? 'Não se aplica' : (selectedTransaction.category?.name || 'Sem categoria') }}</dd>
            </div>
            <div>
              <dt>Data e hora</dt>
              <dd>{{ formatDateTime(selectedTransaction.occurred_at) }}</dd>
            </div>
            <div>
              <dt>Origem</dt>
              <dd>{{ sourceLabel(selectedTransaction.source) }}</dd>
            </div>
          </dl>
        </template>

        <div class="raw-message-card" :class="{ 'is-empty': !selectedTransaction.raw_message }">
          <div class="raw-message-card__heading">
            <span><i class="ph ph-chat-circle-dots" aria-hidden="true"></i></span>
            <div>
              <strong>O que foi dito à IA</strong>
              <small>Mensagem original do lançamento</small>
            </div>
          </div>
          <blockquote v-if="selectedTransaction.raw_message">“{{ selectedTransaction.raw_message }}”</blockquote>
          <p v-else>Este lançamento foi registrado manualmente, sem uma mensagem original.</p>
        </div>

        <div v-if="selectedTransaction.is_recurring" class="transaction-detail__recurring">
          <i class="ph ph-arrows-clockwise" aria-hidden="true"></i>
          Lançamento recorrente
        </div>
      </section>
    </div>

    <div v-if="pendingDelete" class="delete-dialog" role="alertdialog" aria-modal="true" aria-labelledby="deleteDialogTitle">
      <div class="delete-dialog__backdrop" @click="cancelDelete"></div>
      <div class="delete-dialog__content">
        <span class="delete-dialog__icon"><i class="ph ph-trash" aria-hidden="true"></i></span>
        <h2 id="deleteDialogTitle">Remover lançamento?</h2>
        <p>
          “{{ transactionDescription(pendingDelete) }}” será removido do seu histórico.
        </p>
        <div class="delete-dialog__actions">
          <button type="button" :disabled="deleting" @click="cancelDelete">Cancelar</button>
          <button class="is-danger" type="button" :disabled="deleting" @click="confirmDelete">
            {{ deleting ? 'Removendo...' : 'Sim, remover' }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";
import FinancialPrivacyButton from "../../components/Financial/FinancialPrivacyButton.vue";

interface FinanceTransaction {
  owner?: { name: string };
  can_edit?: boolean;
  id: number;
  amount: number | string;
  type: "expense" | "income";
  description?: string | null;
  raw_message?: string | null;
  source?: string | null;
  occurred_at?: string | null;
  is_recurring?: boolean;
  category?: { id?: number | null; name?: string | null; color?: string | null } | null;
}

interface TransactionGroup {
  key: string;
  label: string;
  items: FinanceTransaction[];
}

export default defineComponent({
  name: "FinancialManagementPage",
  components: { FinancialPrivacyButton },
  data() {
    const now = new Date();
    return {
      saving: false,
      deleting: false,
      feedback: "",
      feedbackClass: "transactions-feedback--success",
      showTransactionModal: false,
      showFilters: false,
      pendingDelete: null as FinanceTransaction | null,
      selectedTransaction: null as FinanceTransaction | null,
      detailEditing: false,
      detailSaving: false,
      detailFeedback: "",
      detailAmountInput: "",
      detailForm: {
        category_id: null as number | null,
        description: "",
      },
      amountInput: "",
      form: {
        amount: "",
        type: "expense" as "expense" | "income",
        category_id: null as number | null,
        description: "",
        occurred_at: "",
        is_recurring: false,
      },
      filters: {
        month: now.getMonth() + 1 as number | null,
        year: now.getFullYear(),
        page: 1,
        per_page: 10,
      },
      months: [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
      ].map((label, index) => ({ label, value: index + 1 })),
    };
  },
  computed: {
    ...mapGetters([
      "getFinanceTransactions",
      "getFinanceTransactionsPagination",
      "getFinanceCategories",
      "getFinanceValuesVisible",
      "getLoadingFinance",
      "getFinanceError",
    ]),
    currentPage(): number {
      return Number((this as any).getFinanceTransactionsPagination?.current_page || 1);
    },
    lastPage(): number {
      return Number((this as any).getFinanceTransactionsPagination?.last_page || 1);
    },
    paginationPages(): number[] {
      const lastPage = (this as any).lastPage;
      const currentPage = (this as any).currentPage;
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(lastPage, currentPage + 2);
      const pages: number[] = [];
      for (let page = start; page <= end; page += 1) pages.push(page);
      return pages;
    },
    paginationSummary(): string {
      const pagination = (this as any).getFinanceTransactionsPagination || {};
      const total = Number(pagination.total || 0);
      const from = pagination.from || (total ? (((this as any).currentPage - 1) * Number(pagination.per_page || 10)) + 1 : 0);
      const to = pagination.to || Math.min(total, (this as any).currentPage * Number(pagination.per_page || 10));
      if (!total) return "Nenhum lançamento encontrado";
      return `${from}–${to} de ${total} lançamentos`;
    },
    activePeriodLabel(): string {
      const month = this.months.find((item) => item.value === this.filters.month)?.label;
      return month ? `${month} de ${this.filters.year}` : `Todos de ${this.filters.year}`;
    },
    modalTitle(): string {
      return this.form.type === "income" ? "Nova entrada" : "Novo gasto";
    },
    modalSubmitLabel(): string {
      return this.form.type === "income" ? "Salvar entrada" : "Salvar gasto";
    },
    transactionGroups(): TransactionGroup[] {
      const groups = new Map<string, FinanceTransaction[]>();
      const transactions = (this.getFinanceTransactions || []) as FinanceTransaction[];

      transactions.forEach((transaction) => {
        const date = this.parseDate(transaction.occurred_at);
        const key = date
          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
          : "sem-data";
        const current = groups.get(key) || [];
        current.push(transaction);
        groups.set(key, current);
      });

      return Array.from(groups.entries()).map(([key, items]) => ({
        key,
        label: this.groupDateLabel(items[0]?.occurred_at),
        items,
      }));
    },
  },
  watch: {
    "$route.query.new"(value) {
      if ((value === "expense" || value === "income") && !this.showTransactionModal) {
        this.openTransactionModal(value);
      }
    },
  },
  mounted() {
    this.loadData();
    const requestedType = this.$route.query.new;
    if (requestedType === "expense" || requestedType === "income") this.openTransactionModal(requestedType);
  },
  methods: {
    ...mapActions([
      "fetchFinanceTransactions",
      "fetchFinanceCategories",
      "createFinanceTransaction",
      "updateFinanceTransaction",
      "deleteFinanceTransaction",
    ]),
    openTransactionModal(type: "expense" | "income" = "expense") {
      this.feedback = "";
      this.resetForm(type);
      this.showTransactionModal = true;
    },
    closeTransactionModal() {
      if (this.saving) return;
      this.showTransactionModal = false;
      this.clearNewTransactionQuery();
    },
    clearNewTransactionQuery() {
      if (this.$route.query.new !== "expense" && this.$route.query.new !== "income") return;
      const query = { ...this.$route.query };
      delete query.new;
      void this.$router.replace({ query, hash: this.$route.hash });
    },
    resetForm(type: "expense" | "income" = "expense") {
      this.amountInput = "";
      this.form.amount = "";
      this.form.type = type;
      this.form.category_id = null;
      this.form.description = "";
      this.form.occurred_at = "";
      this.form.is_recurring = false;
    },
    async loadData() {
      await Promise.all([
        (this as any).fetchFinanceCategories(),
        (this as any).fetchFinanceTransactions(this.requestParams()),
      ]);
    },
    requestParams(page?: number) {
      const selectedPage = page || this.filters.page;
      const params: any = { page: selectedPage, per_page: this.filters.per_page };
      if (this.filters.month) params.month = this.filters.month;
      if (this.filters.year) params.year = this.filters.year;
      return params;
    },
    async submitTransaction() {
      const amount = this.parseCurrencyInput(this.amountInput);
      if (amount === null || amount <= 0) {
        this.feedback = "Informe um valor válido maior que zero.";
        this.feedbackClass = "transactions-feedback--error";
        return;
      }

      this.saving = true;
      this.feedback = "";
      try {
        await (this as any).createFinanceTransaction({
          ...this.form,
          amount: amount.toFixed(2),
          category_id: this.form.type === 'expense' ? (this.form.category_id || null) : null,
          occurred_at: this.form.occurred_at || null,
        });
        this.resetForm(this.form.type);
        this.showTransactionModal = false;
        this.clearNewTransactionQuery();
      } catch (error) {
        this.feedback = "Não foi possível salvar o lançamento.";
        this.feedbackClass = "transactions-feedback--error";
      } finally {
        this.saving = false;
      }
    },
    sanitizeAmountInput(event: Event) {
      const input = event.target as HTMLInputElement;
      const digits = input.value
        .replace(/\D/g, "")
        .replace(/^0+(?=\d)/, "")
        .slice(0, 12);
      const formatted = digits
        ? this.formatCurrencyValue(Number(digits) / 100)
        : "";
      this.amountInput = formatted;
      if (input.value !== formatted) input.value = formatted;
    },
    handleAmountPaste(event: ClipboardEvent) {
      const amount = this.parseCurrencyInput(event.clipboardData?.getData("text") || "");
      if (amount === null || amount <= 0) {
        this.amountInput = "";
        return;
      }
      this.amountInput = this.formatCurrencyValue(amount);
    },
    formatCurrencyValue(amount: number): string {
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    parseCurrencyInput(value: string): number | null {
      const cleaned = String(value || "").replace(/\s/g, "");
      if (!/\d/.test(cleaned)) return null;

      const commaIndex = cleaned.lastIndexOf(",");
      const dotIndex = cleaned.lastIndexOf(".");
      let normalized = "";

      if (commaIndex >= 0 && dotIndex >= 0) {
        const decimalIndex = Math.max(commaIndex, dotIndex);
        const integer = cleaned.slice(0, decimalIndex).replace(/\D/g, "") || "0";
        const decimal = cleaned.slice(decimalIndex + 1).replace(/\D/g, "").slice(0, 2);
        normalized = `${integer}.${decimal.padEnd(2, "0")}`;
      } else if (commaIndex >= 0) {
        const integer = cleaned.slice(0, commaIndex).replace(/\D/g, "") || "0";
        const decimal = cleaned.slice(commaIndex + 1).replace(/\D/g, "").slice(0, 2);
        normalized = `${integer}.${decimal.padEnd(2, "0")}`;
      } else if (dotIndex >= 0) {
        const decimalPart = cleaned.slice(dotIndex + 1).replace(/\D/g, "");
        const dotCount = (cleaned.match(/\./g) || []).length;
        if (decimalPart.length > 0 && decimalPart.length <= 2) {
          const integer = cleaned.slice(0, dotIndex).replace(/\D/g, "") || "0";
          normalized = `${integer}.${decimalPart.padEnd(2, "0")}`;
        } else if (dotCount >= 1) {
          normalized = cleaned.replace(/\D/g, "");
        }
      } else {
        normalized = cleaned.replace(/\D/g, "");
      }

      const amount = Number(normalized);
      return Number.isFinite(amount) && amount <= 9999999999.99 ? amount : null;
    },
    requestDelete(transaction: FinanceTransaction) {
      if (transaction.can_edit === false) return;
      this.pendingDelete = transaction;
    },
    openTransactionDetails(transaction: FinanceTransaction) {
      this.selectedTransaction = transaction;
      this.detailEditing = false;
      this.detailFeedback = "";
    },
    closeTransactionDetails() {
      if (this.detailSaving) return;
      this.selectedTransaction = null;
      this.detailEditing = false;
      this.detailFeedback = "";
    },
    startTransactionEdit() {
      if (!this.selectedTransaction || this.selectedTransaction.can_edit === false) return;
      this.detailAmountInput = this.formatCurrencyValue(Number(this.selectedTransaction.amount) || 0);
      this.detailForm.category_id = this.selectedTransaction.type === "expense"
        ? (this.selectedTransaction.category?.id || null)
        : null;
      this.detailForm.description = this.transactionDescription(this.selectedTransaction) === "Sem descrição"
        ? ""
        : this.transactionDescription(this.selectedTransaction);
      this.detailFeedback = "";
      this.detailEditing = true;
    },
    cancelTransactionEdit() {
      if (this.detailSaving) return;
      this.detailEditing = false;
      this.detailFeedback = "";
    },
    sanitizeDetailAmountInput(event: Event) {
      const input = event.target as HTMLInputElement;
      const digits = input.value
        .replace(/\D/g, "")
        .replace(/^0+(?=\d)/, "")
        .slice(0, 12);
      const formatted = digits ? this.formatCurrencyValue(Number(digits) / 100) : "";
      this.detailAmountInput = formatted;
      if (input.value !== formatted) input.value = formatted;
    },
    handleDetailAmountPaste(event: ClipboardEvent) {
      const amount = this.parseCurrencyInput(event.clipboardData?.getData("text") || "");
      this.detailAmountInput = amount !== null && amount > 0 ? this.formatCurrencyValue(amount) : "";
    },
    async saveTransactionEdit() {
      if (!this.selectedTransaction || this.selectedTransaction.can_edit === false) return;
      const amount = this.parseCurrencyInput(this.detailAmountInput);
      if (amount === null || amount <= 0) {
        this.detailFeedback = "Informe um valor válido maior que zero.";
        return;
      }

      this.detailSaving = true;
      this.detailFeedback = "";
      try {
        const updated = await (this as any).updateFinanceTransaction({
          id: this.selectedTransaction.id,
          data: {
            amount: amount.toFixed(2),
            category_id: this.selectedTransaction.type === "expense" ? (this.detailForm.category_id || null) : null,
            description: this.detailForm.description.trim() || null,
          },
        });
        this.selectedTransaction = updated;
        this.detailEditing = false;
      } catch (error) {
        this.detailFeedback = "Não foi possível atualizar o lançamento.";
      } finally {
        this.detailSaving = false;
      }
    },
    cancelDelete() {
      if (this.deleting) return;
      this.pendingDelete = null;
    },
    async confirmDelete() {
      if (!this.pendingDelete) return;
      this.deleting = true;
      try {
        await this.deleteFinanceTransaction(this.pendingDelete.id);
        this.pendingDelete = null;
        if (this.getFinanceTransactions.length === 0 && this.filters.page > 1) this.goToPage(this.filters.page - 1);
      } finally {
        this.deleting = false;
      }
    },
    applyFilters() {
      this.filters.page = 1;
      this.showFilters = false;
      (this as any).fetchFinanceTransactions(this.requestParams());
    },
    resetFilters() {
      const now = new Date();
      this.filters.month = now.getMonth() + 1;
      this.filters.year = now.getFullYear();
      this.filters.page = 1;
      (this as any).fetchFinanceTransactions(this.requestParams());
      this.showFilters = false;
    },
    goToPage(page: number) {
      if (page < 1 || page > (this as any).lastPage || page === (this as any).currentPage) return;
      this.filters.page = page;
      window.scrollTo({ top: 0, behavior: "smooth" });
      (this as any).fetchFinanceTransactions(this.requestParams(page));
    },
    money(value: number | string) {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value) || 0);
    },
    privateValue(value: string) {
      return (this as any).getFinanceValuesVisible ? value : "••••••";
    },
    transactionIconStyle(transaction: FinanceTransaction) {
      if (transaction.type !== "expense") return undefined;
      const color = transaction.category?.color?.trim();
      if (!color || !/^#[\da-f]{6}$/i.test(color)) return undefined;

      const red = Number.parseInt(color.slice(1, 3), 16);
      const green = Number.parseInt(color.slice(3, 5), 16);
      const blue = Number.parseInt(color.slice(5, 7), 16);
      return {
        color,
        backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.14)`,
      };
    },
    displayDescription(value?: string | null) {
      if (!value) return "Sem descrição";
      const descriptions: Record<string, string> = { "Lancamento via WhatsApp": "Lançamento via WhatsApp" };
      return descriptions[value] || value;
    },
    transactionDescription(transaction: FinanceTransaction) {
      const description = transaction.description?.trim();
      const genericDescriptions = ["Lancamento via WhatsApp", "Lançamento via WhatsApp"];
      if (description && !genericDescriptions.includes(description)) return this.displayDescription(description);

      const rawMessage = transaction.raw_message?.trim();
      if (rawMessage) return rawMessage;

      return this.displayDescription(description);
    },
    sourceLabel(value?: string | null) {
      const labels: Record<string, string> = { api: "Manual", whatsapp: "WhatsApp", voice: "Voz" };
      if (!value) return "Manual";
      return labels[value.toLowerCase()] || value;
    },
    parseDate(value?: string | null) {
      if (!value) return null;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    formatDateTime(value?: string | null) {
      const date = this.parseDate(value);
      return date ? new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(date) : "Sem data";
    },
    formatTime(value?: string | null) {
      const date = this.parseDate(value);
      return date ? new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(date) : "--:--";
    },
    groupDateLabel(value?: string | null) {
      const date = this.parseDate(value);
      if (!date) return "Sem data";
      const today = new Date();
      const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
      const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
      const dayDifference = Math.round((startToday - startDate) / 86400000);
      if (dayDifference === 0) return "Hoje";
      if (dayDifference === 1) return "Ontem";
      return new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "2-digit", month: "long" }).format(date);
    },
  },
});
</script>

<style scoped>
.transactions-page {
  --finance-accent: #06a77d;
  --finance-accent-dark: #05785d;
  --finance-ink: #171629;
  --finance-muted: #747487;
  --finance-surface: #ffffff;
  --finance-soft: #f5f5f8;
  --finance-border: #e9e9f0;
  color: var(--finance-ink);
  margin: 0 auto;
  max-width: 1480px;
  width: 100%;
}

.transactions-header {
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.transactions-eyebrow {
  color: var(--finance-accent-dark);
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.transactions-header h1 {
  color: var(--finance-ink);
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  letter-spacing: -0.04em;
  margin: 0;
}

.transactions-header p {
  color: var(--finance-muted);
  font-size: 14px;
  margin: 5px 0 0;
}

.refresh-button {
  align-items: center;
  background: var(--finance-surface);
  border: 1px solid var(--finance-border);
  border-radius: 50%;
  color: var(--finance-ink);
  display: inline-flex;
  flex: 0 0 44px;
  font-size: 20px;
  height: 44px;
  justify-content: center;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.refresh-button:hover:not(:disabled) {
  border-color: var(--finance-accent);
  color: var(--finance-accent-dark);
  transform: translateY(-1px);
}

.refresh-button:disabled { cursor: wait; opacity: 0.65; }
.is-spinning { animation: finance-spin 0.9s linear infinite; }

.create-actions {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 18px;
}

.create-action {
  align-items: center;
  background: var(--finance-surface);
  border: 1px solid var(--finance-border);
  border-radius: 18px;
  color: var(--finance-ink);
  display: flex;
  gap: 12px;
  min-height: 72px;
  padding: 12px 15px;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.create-action:hover {
  box-shadow: 0 10px 26px rgba(23, 22, 41, 0.08);
  transform: translateY(-2px);
}

.create-action--expense:hover { border-color: rgba(239, 106, 103, 0.45); }
.create-action--income:hover { border-color: rgba(6, 167, 125, 0.45); }

.create-action__icon,
.transaction-type-icon {
  align-items: center;
  border-radius: 14px;
  display: inline-flex;
  flex: 0 0 42px;
  font-size: 20px;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.create-action--expense .create-action__icon,
.transaction-type-icon.is-expense { background: #fff0ee; color: #c74f4a; }
.create-action--income .create-action__icon,
.transaction-type-icon.is-income { background: #e8f8f3; color: var(--finance-accent-dark); }

.create-action small,
.create-action strong { display: block; }
.create-action small { color: var(--finance-muted); font-size: 10px; font-weight: 700; text-transform: uppercase; }
.create-action strong { color: var(--finance-ink); font-size: 15px; font-weight: 800; margin-top: 2px; }

.transactions-panel {
  background: var(--finance-surface);
  border: 1px solid var(--finance-border);
  border-radius: 22px;
  padding: 22px;
}

.transactions-toolbar {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
}

.transactions-toolbar h2 {
  color: var(--finance-ink);
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.025em;
  margin: 0;
}

.transactions-toolbar__summary {
  color: var(--finance-muted);
  display: block;
  font-size: 12px;
  margin-top: 4px;
}

.filter-toggle {
  align-items: center;
  background: #e8f8f3;
  border: 1px solid #c9eee2;
  border-radius: 999px;
  color: var(--finance-accent-dark);
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  min-height: 36px;
  padding: 0 12px;
  white-space: nowrap;
}

.filter-toggle i { font-size: 15px; line-height: 1; }

.filters-panel {
  align-items: end;
  background: var(--finance-soft);
  border: 1px solid var(--finance-border);
  border-radius: 16px;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(180px, 1fr) 130px auto;
  margin-top: 18px;
  padding: 15px;
}

.filters-panel label > span,
.form-field > span,
.amount-field > span {
  color: var(--finance-ink);
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 7px;
}

.filters-panel .form-select,
.filters-panel .form-control,
.transaction-modal .form-select,
.transaction-modal .form-control {
  border: 1px solid var(--finance-border);
  border-radius: 12px;
  box-shadow: none;
  min-height: 44px;
}

.filters-panel__actions { display: flex; gap: 8px; }
.filter-reset,
.filter-apply {
  border-radius: 12px;
  font-size: 12px;
  font-weight: 800;
  min-height: 44px;
  padding: 0 14px;
}
.filter-reset { background: transparent; border: 1px solid var(--finance-border); color: var(--finance-muted); }
.filter-apply { align-items: center; background: var(--finance-accent); border: 1px solid var(--finance-accent); color: #fff; display: inline-flex; gap: 6px; }

.transactions-feedback {
  align-items: center;
  border-radius: 14px;
  display: flex;
  font-size: 13px;
  gap: 12px;
  justify-content: space-between;
  margin-top: 16px;
  padding: 12px 14px;
}
.transactions-feedback > div { align-items: center; display: flex; gap: 8px; }
.transactions-feedback button { background: transparent; border: 0; color: inherit; font-weight: 800; }
.transactions-feedback--error { background: #fff1f0; border: 1px solid #ffd5d1; color: #9c3732; }
.transactions-feedback--success { background: #e8f8f3; border: 1px solid #c9eee2; color: var(--finance-accent-dark); }

.desktop-transactions-table { margin-top: 24px; overflow-x: auto; }
.finance-data-table { border-collapse: collapse; color: var(--finance-ink); min-width: 850px; width: 100%; }
.finance-data-table th { color: var(--finance-muted); font-size: 10px; font-weight: 800; letter-spacing: 0.06em; padding: 0 12px 12px; text-transform: uppercase; white-space: nowrap; }
.finance-data-table td { border-top: 1px solid var(--finance-border); color: var(--finance-ink); font-size: 13px; font-weight: 600; padding: 14px 12px; vertical-align: middle; }
.finance-data-table th:first-child,
.finance-data-table td:first-child { padding-left: 0; }
.finance-data-table th:last-child,
.finance-data-table td:last-child { padding-right: 0; }

.transaction-table-row {
  cursor: pointer;
  outline: 0;
}

.transaction-table-row td {
  transition: background-color 0.2s ease;
}

.transaction-table-row:hover td,
.transaction-table-row:focus-visible td {
  background: rgba(6, 167, 125, 0.055);
}

.table-description { align-items: center; display: flex; gap: 11px; max-width: 380px; min-width: 210px; }
.table-description .transaction-type-icon { border-radius: 11px; flex-basis: 36px; font-size: 17px; height: 36px; width: 36px; }
.table-description div { min-width: 0; }
.table-description strong,
.table-description small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.table-description strong { color: var(--finance-ink); font-size: 13px; font-weight: 800; }
.table-description small { color: var(--finance-muted); font-size: 10px; margin-top: 2px; }

.source-badge { background: var(--finance-soft); border-radius: 999px; color: var(--finance-muted); display: inline-block; font-size: 10px; font-weight: 800; padding: 5px 8px; }
.transaction-amount { font-weight: 800; white-space: nowrap; }
.transaction-amount.is-income { color: var(--finance-accent-dark); }
.transaction-amount.is-expense { color: #a94541; }

.delete-button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 10px;
  color: #a3a2b0;
  display: inline-flex;
  font-size: 17px;
  height: 36px;
  justify-content: center;
  padding: 0;
  transition: background-color 0.2s ease, color 0.2s ease;
  width: 36px;
}
.delete-button:hover { background: #fff0ee; color: #c74f4a; }

.mobile-transactions-list { display: none; }

.pagination-row {
  align-items: center;
  border-top: 1px solid var(--finance-border);
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 16px;
}
.pagination-row__summary { color: var(--finance-muted); font-size: 12px; }
.pagination-actions { display: flex; gap: 6px; }
.pagination-actions button,
.pagination-mobile button { background: var(--finance-surface); border: 1px solid var(--finance-border); border-radius: 9px; color: var(--finance-muted); font-size: 11px; font-weight: 800; min-height: 34px; min-width: 34px; padding: 0 9px; }
.pagination-actions button.active { background: var(--finance-accent); border-color: var(--finance-accent); color: #fff; }
.pagination-actions button:disabled,
.pagination-mobile button:disabled { cursor: not-allowed; opacity: 0.45; }
.pagination-mobile { display: none; }

.empty-transactions { align-items: center; display: flex; flex-direction: column; padding: 54px 16px 40px; text-align: center; }
.empty-transactions__icon { align-items: center; background: #e8f8f3; border-radius: 50%; color: var(--finance-accent-dark); display: inline-flex; font-size: 28px; height: 62px; justify-content: center; margin-bottom: 14px; width: 62px; }
.empty-transactions strong { color: var(--finance-ink); font-size: 17px; }
.empty-transactions p { color: var(--finance-muted); margin: 6px 0 16px; }
.empty-transactions button { background: var(--finance-accent); border: 0; border-radius: 12px; color: #fff; font-weight: 800; min-height: 42px; padding: 0 16px; }

.transaction-skeletons { display: grid; gap: 12px; margin-top: 24px; }
.transaction-skeleton { align-items: center; border-top: 1px solid var(--finance-border); display: grid; gap: 12px; grid-template-columns: 42px 1fr 90px; padding: 14px 0 2px; }
.skeleton { animation: skeleton-pulse 1.4s ease-in-out infinite; background: #e8e8ee; border-radius: 999px; display: block; }
.skeleton--icon { height: 42px; width: 42px; }
.skeleton--content { height: 16px; width: min(280px, 75%); }
.skeleton--amount { height: 16px; width: 90px; }

.transaction-modal,
.transaction-detail,
.delete-dialog { align-items: center; display: flex; inset: 0; justify-content: center; padding: 20px; position: fixed; z-index: 1050; }
.transaction-modal__backdrop,
.transaction-detail__backdrop,
.delete-dialog__backdrop { background: rgba(17, 24, 39, 0.52); inset: 0; position: absolute; }
.transaction-modal__content,
.transaction-detail__content,
.delete-dialog__content { background: var(--finance-surface); box-shadow: 0 24px 80px rgba(15, 23, 42, 0.24); position: relative; z-index: 1; }
.transaction-modal__content { border-radius: 22px; max-height: calc(100vh - 40px); max-width: 590px; overflow-y: auto; padding: 24px; width: min(100%, 590px); }
.transaction-modal__handle { display: none; }
.transaction-modal__header { align-items: flex-start; display: flex; justify-content: space-between; margin-bottom: 18px; }
.transaction-modal__header h2 { color: var(--finance-ink); font-size: 23px; font-weight: 800; letter-spacing: -0.03em; margin: 0; }
.transaction-modal__close { align-items: center; background: var(--finance-soft); border: 0; border-radius: 50%; color: var(--finance-ink); display: inline-flex; font-size: 18px; height: 38px; justify-content: center; width: 38px; }

.transaction-type-selector { background: var(--finance-soft); border-radius: 14px; display: grid; gap: 5px; grid-template-columns: 1fr 1fr; margin-bottom: 18px; padding: 5px; }
.transaction-type-selector button { align-items: center; background: transparent; border: 0; border-radius: 11px; color: var(--finance-muted); display: flex; font-size: 13px; font-weight: 800; gap: 6px; justify-content: center; min-height: 40px; }
.transaction-type-selector button.active { background: var(--finance-surface); box-shadow: 0 3px 12px rgba(23, 22, 41, 0.08); color: var(--finance-ink); }

.amount-field { display: block; margin-bottom: 17px; }
.amount-field > div { align-items: center; border-bottom: 2px solid var(--finance-border); display: flex; gap: 8px; padding: 2px 0 8px; transition: border-color 0.2s ease; }
.amount-field > div:focus-within { border-color: var(--finance-accent); }
.amount-field > div > span { color: var(--finance-muted); font-size: 21px; font-weight: 800; }
.amount-field input { background: transparent; border: 0; color: var(--finance-ink); font-size: 32px; font-weight: 800; letter-spacing: -0.04em; min-width: 0; outline: 0; width: 100%; }
.amount-field input::placeholder { color: #c3c2cb; }

.transaction-form-grid { display: grid; gap: 12px; grid-template-columns: 1fr 1fr; }
.form-field { display: block; margin-bottom: 15px; }
.form-field--wide { grid-column: 1 / -1; }
.recurring-check { align-items: center; color: var(--finance-muted); display: flex; font-size: 13px; gap: 8px; margin: 2px 0 18px; }
.recurring-check input { border-color: var(--finance-border); height: 18px; margin: 0; width: 18px; }

.save-transaction-button { align-items: center; background: var(--finance-accent); border: 0; border-radius: 13px; color: #fff; display: flex; font-size: 14px; font-weight: 800; justify-content: space-between; min-height: 48px; padding: 0 17px; width: 100%; }
.save-transaction-button:disabled { opacity: 0.65; }

.transaction-detail__content {
  border-radius: 22px;
  max-height: calc(100vh - 40px);
  max-width: 540px;
  overflow-y: auto;
  padding: 24px;
  width: min(100%, 540px);
}

.transaction-detail__handle { display: none; }

.transaction-detail__header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.transaction-detail__header h2 {
  color: var(--finance-ink);
  font-size: 23px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.transaction-detail__header button {
  align-items: center;
  background: var(--finance-soft);
  border: 0;
  border-radius: 50%;
  color: var(--finance-ink);
  display: inline-flex;
  font-size: 18px;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.transactions-header__actions {
  align-items: center;
  display: flex;
  gap: 8px;
}

.transaction-detail__header-actions { align-items: center; display: flex; gap: 8px; }
.transaction-detail__header .transaction-detail__edit-button {
  border-radius: 12px;
  gap: 6px;
  padding: 0 13px;
  width: auto;
}
.transaction-detail__edit-button span { font-size: 12px; font-weight: 800; }

.transaction-detail__amount {
  align-items: center;
  background: var(--finance-soft);
  border: 1px solid var(--finance-border);
  border-radius: 17px;
  display: flex;
  gap: 13px;
  margin-bottom: 17px;
  padding: 15px;
}

.transaction-detail__amount > div > span,
.transaction-detail__amount > div > strong { display: block; }
.transaction-detail__amount > div > span { color: var(--finance-muted); font-size: 11px; font-weight: 800; text-transform: uppercase; }
.transaction-detail__amount > div > strong { color: var(--finance-ink); font-size: 25px; font-weight: 800; letter-spacing: -0.03em; margin-top: 2px; }
.transaction-detail__amount.is-income > div > strong { color: var(--finance-accent-dark); }
.transaction-detail__amount.is-expense > div > strong { color: #a94541; }

.transaction-detail__grid {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  margin: 0 0 17px;
}

.transaction-detail__grid > div {
  border: 1px solid var(--finance-border);
  border-radius: 13px;
  min-width: 0;
  padding: 12px;
}

.transaction-detail__grid dt {
  color: var(--finance-muted);
  font-size: 10px;
  font-weight: 800;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.transaction-detail__grid dd {
  color: var(--finance-ink);
  font-size: 13px;
  font-weight: 800;
  margin: 0;
  overflow-wrap: anywhere;
}

.transaction-detail__edit-form .form-field > span,
.transaction-detail__amount-field > span {
  color: var(--finance-muted);
  display: block;
  font-size: 10px;
  font-weight: 800;
  margin-bottom: 6px;
  text-transform: uppercase;
}
.transaction-detail__edit-form textarea { line-height: 1.45; min-height: 82px; resize: vertical; }
.transaction-detail__amount-field { margin-bottom: 15px; }
.transaction-detail__amount-field input { font-size: 27px; }
.transaction-detail__grid--metadata { margin-top: 2px; }
.transaction-detail__grid--metadata > div {
  background: var(--finance-soft);
  border-color: transparent;
  cursor: not-allowed;
  opacity: 0.52;
}
.transaction-detail__grid--metadata dt,
.transaction-detail__grid--metadata dd { color: var(--finance-muted); }
.transaction-detail__grid--metadata dd { font-weight: 700; }
.transaction-detail__edit-actions { display: grid; gap: 9px; grid-template-columns: 1fr 1.35fr; margin-bottom: 12px; }
.transaction-detail__edit-actions button {
  align-items: center;
  background: var(--finance-soft);
  border: 0;
  border-radius: 12px;
  color: var(--finance-ink);
  display: flex;
  font-size: 13px;
  font-weight: 800;
  gap: 7px;
  justify-content: center;
  min-height: 46px;
  padding: 0 14px;
}
.transaction-detail__edit-actions button.is-primary { background: var(--finance-accent); color: #fff; }
.transaction-detail__edit-actions button:disabled { opacity: 0.65; }

.raw-message-card {
  background: #effaf6;
  border: 1px solid #c9eee2;
  border-radius: 17px;
  padding: 15px;
}

.raw-message-card__heading {
  align-items: center;
  display: flex;
  gap: 10px;
}

.raw-message-card__heading > span {
  align-items: center;
  background: var(--finance-accent);
  border-radius: 11px;
  color: #fff;
  display: inline-flex;
  flex: 0 0 36px;
  font-size: 18px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.raw-message-card__heading strong,
.raw-message-card__heading small { display: block; }
.raw-message-card__heading strong { color: var(--finance-ink); font-size: 13px; font-weight: 800; }
.raw-message-card__heading small { color: var(--finance-muted); font-size: 10px; margin-top: 2px; }

.raw-message-card blockquote,
.raw-message-card p {
  color: var(--finance-ink);
  font-size: 14px;
  line-height: 1.55;
  margin: 13px 0 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.raw-message-card.is-empty {
  background: var(--finance-soft);
  border-color: var(--finance-border);
}

.raw-message-card.is-empty .raw-message-card__heading > span {
  background: #dedde5;
  color: var(--finance-muted);
}

.raw-message-card.is-empty p {
  color: var(--finance-muted);
  font-size: 12px;
}

.transaction-detail__recurring {
  align-items: center;
  color: var(--finance-muted);
  display: flex;
  font-size: 11px;
  font-weight: 800;
  gap: 6px;
  margin-top: 13px;
}

.delete-dialog__content { border-radius: 22px; max-width: 400px; padding: 26px; text-align: center; width: 100%; }
.delete-dialog__icon { align-items: center; background: #fff0ee; border-radius: 50%; color: #c74f4a; display: inline-flex; font-size: 24px; height: 56px; justify-content: center; margin-bottom: 14px; width: 56px; }
.delete-dialog h2 { color: var(--finance-ink); font-size: 20px; font-weight: 800; margin: 0 0 8px; }
.delete-dialog p { color: var(--finance-muted); line-height: 1.5; margin: 0 0 20px; overflow-wrap: anywhere; }
.delete-dialog__actions { display: grid; gap: 9px; grid-template-columns: 1fr 1fr; }
.delete-dialog__actions button { background: var(--finance-soft); border: 0; border-radius: 12px; color: var(--finance-ink); font-weight: 800; min-height: 44px; }
.delete-dialog__actions button.is-danger { background: #c74f4a; color: #fff; }

@media (max-width: 991px) {
  .filters-panel { grid-template-columns: 1fr 120px; }
  .filters-panel__actions { grid-column: 1 / -1; justify-content: flex-end; }
}

@media (max-width: 767px) {
  .transactions-header { margin-bottom: 14px; }
  .transactions-header h1 { font-size: 24px; }
  .transactions-header p { font-size: 12px; line-height: 1.4; max-width: 285px; }

  .create-actions { gap: 9px; margin-bottom: 14px; }
  .create-action { border-radius: 17px; gap: 9px; min-height: 70px; padding: 10px; }
  .create-action__icon { border-radius: 12px; flex-basis: 38px; font-size: 18px; height: 38px; width: 38px; }
  .create-action small { font-size: 9px; }
  .create-action strong { font-size: 13px; }

  .transactions-panel { border-radius: 20px; padding: 17px 15px; }
  .transactions-toolbar h2 { font-size: 19px; }
  .transactions-toolbar__summary { font-size: 11px; }
  .filter-toggle { font-size: 10px; gap: 4px; min-height: 32px; max-width: 170px; overflow: hidden; padding: 0 9px; }
  .filter-toggle span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .filter-toggle i { flex: 0 0 auto; font-size: 13px; }

  .filters-panel { grid-template-columns: 1fr 96px; margin-top: 14px; padding: 12px; }
  .filters-panel__actions { display: grid; grid-template-columns: 1fr 1.35fr; }
  .filter-reset,
  .filter-apply { justify-content: center; padding: 0 8px; }

  .desktop-transactions-table { display: none; }
  .mobile-transactions-list { display: block; margin-top: 18px; }
  .transaction-group + .transaction-group { margin-top: 22px; }
  .transaction-group h3 { color: var(--finance-muted); font-size: 11px; font-weight: 800; letter-spacing: 0.05em; margin: 0 0 8px; text-transform: capitalize; }

  .transaction-item { align-items: center; border-bottom: 1px solid var(--finance-border); display: grid; gap: 10px; grid-template-columns: 40px minmax(0, 1fr) auto; padding: 12px 0; }
  .transaction-item { cursor: pointer; outline: 0; transition: background-color 0.2s ease, transform 0.2s ease; }
  .transaction-item:active { transform: scale(0.99); }
  .transaction-item:focus-visible { background: rgba(6, 167, 125, 0.055); }
  .transaction-item:last-child { border-bottom: 0; }
  .transaction-item .transaction-type-icon { border-radius: 12px; flex-basis: 40px; font-size: 18px; height: 40px; width: 40px; }
  .transaction-item__main { min-width: 0; }
  .transaction-item__main > strong { color: var(--finance-ink); display: block; font-size: 13px; font-weight: 800; line-height: 1.25; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .transaction-item__main > span { color: var(--finance-muted); display: block; font-size: 10px; margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .transaction-item__main small { background: var(--finance-soft); border-radius: 999px; color: var(--finance-muted); display: inline-block; font-size: 9px; font-weight: 800; margin-top: 5px; padding: 3px 6px; }
  .transaction-item__side { align-items: flex-end; display: flex; flex-direction: column; gap: 5px; }
  .transaction-item__side .transaction-amount { font-size: 12px; }
  .transaction-item__side .delete-button { font-size: 15px; height: 28px; width: 28px; }

  .pagination-row { display: block; margin-top: 8px; padding-top: 14px; }
  .pagination-row__summary,
  .pagination-actions { display: none; }
  .pagination-mobile { align-items: center; display: flex; justify-content: space-between; }
  .pagination-mobile span { color: var(--finance-muted); font-size: 11px; font-weight: 800; }
  .pagination-mobile button { align-items: center; display: inline-flex; font-size: 15px; justify-content: center; padding: 0; }

  .transaction-skeleton { grid-template-columns: 40px 1fr 65px; }
  .skeleton--amount { width: 65px; }

  .transaction-modal { align-items: flex-end; padding: 0; }
  .transaction-modal__content { border-radius: 24px 24px 0 0; max-height: calc(100vh - 20px); padding: 14px 18px max(20px, calc(14px + env(safe-area-inset-bottom))); width: 100%; }
  .transaction-modal__handle { background: #d5d4dc; border-radius: 999px; display: block; height: 4px; margin: 0 auto 14px; width: 42px; }
  .transaction-modal__header h2 { font-size: 21px; }
  .transaction-modal__close { height: 36px; width: 36px; }
  .transaction-form-grid { grid-template-columns: 1fr; gap: 0; }
  .amount-field input { font-size: 30px; }

  .transaction-detail { align-items: flex-end; padding: 0; }
  .transaction-detail__content { border-radius: 24px 24px 0 0; max-height: calc(100vh - 20px); padding: 14px 18px max(20px, calc(14px + env(safe-area-inset-bottom))); width: 100%; }
  .transaction-detail__handle { background: #d5d4dc; border-radius: 999px; display: block; height: 4px; margin: 0 auto 14px; width: 42px; }
  .transaction-detail__header h2 { font-size: 21px; }
  .transaction-detail__amount { padding: 13px; }
  .transaction-detail__amount > div > strong { font-size: 23px; }
  .transaction-detail__grid { gap: 8px; }
  .transaction-detail__grid > div { padding: 10px; }

  .delete-dialog { align-items: flex-end; padding: 12px; }
  .delete-dialog__content { border-radius: 22px; padding: 24px 20px max(20px, calc(14px + env(safe-area-inset-bottom))); }
}

@media (max-width: 390px) {
  .transactions-header p { max-width: 245px; }
  .create-action { gap: 7px; padding: 8px; }
  .create-action__icon { flex-basis: 34px; height: 34px; width: 34px; }
  .create-action strong { font-size: 12px; }
  .transactions-toolbar { gap: 8px; }
  .filter-toggle { max-width: 145px; }
  .transaction-item { gap: 8px; grid-template-columns: 38px minmax(0, 1fr) auto; }
  .transaction-item .transaction-type-icon { flex-basis: 38px; height: 38px; width: 38px; }
}

@keyframes finance-spin { to { transform: rotate(360deg); } }
@keyframes skeleton-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .is-spinning,
  .skeleton { animation: none; }
  .create-action,
  .refresh-button { transition: none; }
}
</style>

<style>
body.dark .transactions-page {
  --finance-ink: #f5f5f8;
  --finance-muted: #aaa9ba;
  --finance-surface: #29283b;
  --finance-soft: #343347;
  --finance-border: #3b394e;
}

body.dark .transactions-page .create-action--expense .create-action__icon,
body.dark .transactions-page .transaction-type-icon.is-expense,
body.dark .transactions-page .delete-dialog__icon {
  background: rgba(239, 106, 103, 0.14);
  color: #ffaaa8;
}

body.dark .transactions-page .create-action--income .create-action__icon,
body.dark .transactions-page .transaction-type-icon.is-income,
body.dark .transactions-page .empty-transactions__icon,
body.dark .transactions-page .filter-toggle {
  background: rgba(6, 180, 138, 0.15);
  border-color: rgba(102, 224, 189, 0.2);
  color: #66e0bd;
}

body.dark .transactions-page .transactions-feedback--error {
  background: rgba(239, 106, 103, 0.12);
  border-color: rgba(239, 106, 103, 0.28);
  color: #ffaaa8;
}

body.dark .transactions-page .form-control,
body.dark .transactions-page .form-select {
  background-color: #343347;
  border-color: #49475d;
  color: #f5f5f8;
}

body.dark .transactions-page .skeleton { background: #3a394d; }

body.dark .transactions-page .raw-message-card {
  background: rgba(6, 180, 138, 0.12);
  border-color: rgba(102, 224, 189, 0.2);
}

body.dark .transactions-page .raw-message-card.is-empty {
  background: #343347;
  border-color: #49475d;
}
</style>
